import { Box, Text, useToast } from '@chakra-ui/react'
import axios, { AxiosError } from 'axios'
import { ModalBuild } from 'components/molecules'
import { Bar } from 'components/molecules/Bar'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { motion } from 'framer-motion'
import useToastMessages from 'hooks/Toast'
import {
    AgentOrderBy,
    AgentQueryDTO,
    AgentResponseDTO,
    ErrorResponseDTO
} from 'models'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { createAgent, editAgent, getAgent, removeAgent } from 'services/agent'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import FormAgent from './components/FormAgent'
import {
    FormAgentElementsValues,
    ResponseFormAgent
} from './components/FormAgent/interfaces'
import { errorsAgent } from './components/utils/errorsAgent'
import { defaultHeaders, defaultLoaders } from './constants'

const Agent: React.FC = () => {
    const [loading, setLoading] = useState(true)
    const toast = useToast()
    const [filter, setFilter] = useState('')

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [itemEdit, setItemEdit] = useState<any>()
    const { emitToast } = useToastMessages()
    const deleteModalRef = useRef<ModalBuildHandle>(null)
    const [deleteAgentID, setAgentDeleteID] = useState<string>('')
    const [orderBy, setOrderBy] = useState<
        Required<Pick<AgentQueryDTO, 'ordenacao' | 'tipoOrdenacao'>>
    >({
        ordenacao: 'razaoSocial',
        tipoOrdenacao: 'ASC'
    })

    const [table, setTable] = useState<
        TableBuildProps<
            | 'uf'
            | 'codigo'
            | 'cnpj'
            | 'razaoSocial'
            | 'categorizacao'
            | 'dataInicioVigencia'
            | 'dataFimVigencia'
        >
    >({
        header: [...(defaultHeaders as any)],
        action: [
            {
                type: 'EDIT'
            },
            {
                type: 'DELETE'
            }
        ]
    })

    const handlePage = (value: number) => {
        setPage(value)
        setLoading(true)
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setLoading(true)
    }

    const toogleDeleteModal = (open = true): void => {
        if (open) {
            deleteModalRef.current?.onOpen()

            return
        }
        deleteModalRef.current?.onClose()
    }

    const handleAction = (typeAction: string, id: number | string) => {
        const item: any =
            table.body?.length && table.body.find(item => item.id === id)

        switch (typeAction) {
            case 'EDIT':
                setItemEdit({
                    ...item,
                    dataFimVigencia:
                        formatter.formatDateToYearMonthDay(
                            item.dataFimVigencia
                        ) ?? '',
                    dataInicioVigencia:
                        formatter.formatDateToYearMonthDay(
                            item.dataInicioVigencia
                        ) ?? ''
                })

                toogleModalForm()
                break

            case 'DELETE':
                setAgentDeleteID(String(id))
                toogleDeleteModal()
                break
        }
    }

    const somenteNumeros = (valor?: string | null): string => {
        return valor?.replace(/\D/g, '') ?? ''
    }

    const normalizarDadosNotaDebito = (
        dados: FormAgentElementsValues['dadosNotaDebito']
    ) => ({
        ...dados,
        razaoSocial: dados.razaoSocial.trim(),
        cnpj: somenteNumeros(dados.cnpj),
        inscricaoEstadual: dados.inscricaoEstadual?.trim() || null,
        telefone: somenteNumeros(dados.telefone),
        logradouro: dados.logradouro.trim(),
        numeroEndereco: dados.numeroEndereco.trim(),
        complemento: dados.complemento?.trim() || null,
        bairro: dados.bairro.trim(),
        municipio: dados.municipio.trim(),
        codigoMunicipio: dados.codigoMunicipio?.trim() || null,
        uf: dados.uf,
        cep: somenteNumeros(dados.cep)
    })

    const handleOrderBy = (ordenacao: AgentOrderBy) => {
        setOrderBy(old => ({
            ordenacao,
            tipoOrdenacao:
                old.ordenacao !== ordenacao
                    ? old.tipoOrdenacao
                    : old.tipoOrdenacao === 'ASC'
                    ? 'DESC'
                    : 'ASC'
        }))
        setLoading(true)
    }

    const formModalRef = useRef<ModalBuildHandle>(null)

    const toogleModalForm = (): void => {
        formModalRef.current?.onOpen()
    }

    const handleCloseFormModal = (): void => {
        setItemEdit({})
        formModalRef.current?.onClose()
    }

    const fetchEdit = async (data: any) => {
        try {
            await editAgent(data, itemEdit.id)
            handleCloseFormModal()
            fetchAgents()

            emitToast({
                title: 'Editar Agente Financeiro',
                status: 'success',
                description: 'O agente foi editado com sucesso!'
            })

            setItemEdit({})
        } catch (error) {
            emitToast({
                title: 'Erro ao editar Agente Financeiro',
                status: 'error'
            })
        }
    }

    const fetchCreate = async (data: FormAgentElementsValues) => {
        const params: any = {
            ...data,
            expectativaRegistroContratoDiario: data.expectativaRegistroContratoDiario ?? null,
            cnpj: data.cnpj.replace(/\D/g, ''),
            instituicaoPagaTaxa: data?.instituicaoPagaTaxa === 'SIM',
            municipio: data.municipio.split('|')[1],
            codigoMunicipio: +data.municipio.split('|')[0],
            telefone: data?.telefone
                ? formatter.cleanFormatter(data?.telefone)
                : null
        }

        createAgent(params)
            .then(_ => {
                toast({
                    title: 'Agente criado com sucesso!',
                    description:
                        'Vincule o agente a uma instituição financeira',
                    position: 'bottom-left',
                    status: 'info',
                    isClosable: true
                })

                handleCloseFormModal()
                return _
            })
            .catch(({ response }) => {
                const { data } = response as ErrorResponseDTO

                data.filter(
                    (elem, index, arr) =>
                        index ===
                        arr.findIndex(
                            checkEle => checkEle.codigo === elem.codigo
                        )
                ).map(res => {
                    const error = errorsAgent[res.codigo]
                    toast({
                        title: error.title,
                        description: error.description,
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true,
                        variant: 'solid-error'
                    })
                })
            })
    }

    const handleCreate = async ({ data }: ResponseFormAgent) => {
        if (data) {
            const isEdit = itemEdit?.id
            if (isEdit) {
                const { cnpj, uf, ...itemEdit } = data

                fetchEdit({
                    ...itemEdit,
                    instituicaoPagaTaxa: data.instituicaoPagaTaxa === 'SIM',
                    municipio: data.municipio.split('|')[1],
                    codigoMunicipio: +data.municipio.split('|')[0],
                    telefone: data?.telefone
                        ? formatter.cleanFormatter(data.telefone)
                        : null
                })

                return
            }

            fetchCreate(data)
        }
    }

    const toastDeleteErrosMessages = (code: string) => {
        if (code === '1024') {
            toast({
                title: 'Operação não permitida',
                description: 'Agente financeiro já possui contratos',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        } else if (code === '1025') {
            toast({
                title: 'Vínculo existente',
                description:
                    'O agente financeiro já está vinculado a uma instituição financeira',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        } else {
            toast({
                title: 'Failha ao remover agente financeiro.',
                description: 'Tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        }
    }

    const handleDelete = async () => {
        try {
            await removeAgent(deleteAgentID)
            toast({
                title: 'Agente removido!',
                description: 'A lista de agentes foi atualizada.',
                position: 'bottom-left',
                status: 'info',
                isClosable: true
            })
            fetchAgents()
        } catch (e) {
            let code
            if (axios.isAxiosError(e)) {
                const error = e as AxiosError<Array<{ codigo: string }>>
                code = error.response?.data[0]?.codigo
            }
            toastDeleteErrosMessages(code || '')
        } finally {
            toogleDeleteModal(false)
        }
    }

    const handleSearch = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
        setLoading(true)
        const value = e.target.value.toLowerCase()
        setFilter(value)
        setPage(1)
    })

    const buildTable = (agenteData: AgentResponseDTO[]) => {
        return agenteData.map(
            ({
                cnpj,
                categorizacao,
                dataInicioVigencia,
                dataFimVigencia,
                ...restData
            }) => ({
                ...restData,
                cnpj: cnpj ? formatter.cnpj(cnpj) : '',
                categorizacao: formatter.convertCategory(categorizacao),
                dataInicioVigencia: dataInicioVigencia
                    ? formatter.data(dataInicioVigencia)
                    : '',
                dataFimVigencia: dataFimVigencia
                    ? formatter.data(dataFimVigencia)
                    : ''
            })
        )
    }
    const fetchAgents = async (query?: AgentQueryDTO) => {
        try {
            const queryParams = {
                ...query,
                ...orderBy,
                pagina: page,
                limite: limit
            }

            const { data, headers } = await getAgent(queryParams)

            setTable(prevTable => ({
                ...prevTable,
                body: buildTable(data)
            }))
            setTotalPages(+headers['total-pages'])
        } catch (error) {
            const { response } = error as any
            const { status } = response
            const isClientError = String(status).charAt(0) === '4'

            toast({
                title: isClientError
                    ? 'Agente financeiro já existente!'
                    : 'Sentimos muito!',
                description: isClientError
                    ? 'Verifique o código do agente e UF.'
                    : 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAgents(
            filter
                ? {
                      razaoSocial: filter,
                      cnpj: filter.replace(/\D/g, ''),
                      codigo: filter,
                      uf: filter
                  }
                : {}
        )
    }, [filter, page, limit, orderBy])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <Bar
                title="Agentes Financeiros"
                input={{
                    props: {
                        w: '100%',
                        variant: 'flushed',
                        placeholder: 'Buscar pela Razão Social, CNPJ ou código'
                    }
                }}
                action={{
                    text: 'Novo agente financeiro',
                    props: {
                        colorScheme: 'primary'
                    }
                }}
                onChangeInput={handleSearch}
                onClickActions={toogleModalForm}
            />

            <TableBuild
                header={table.header}
                body={table.body}
                ordenation={orderBy}
                action={table.action}
                onAction={handleAction}
                error={{
                    action: {
                        text: filter ? 'search' : 'agent',
                        onClick: () => !filter && toogleModalForm()
                    }
                }}
                loading={{
                    status: loading,
                    loader: defaultLoaders
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onOrderBy={order =>
                    handleOrderBy(order as unknown as AgentOrderBy)
                }
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            />

            <ModalBuild
                ref={formModalRef}
                closeOnOverlayClick={false}
                title=" "
                size="4xl"
                onCancel={handleCloseFormModal}
            >
                <FormAgent
                    itemEdit={itemEdit}
                    onSubmitForm={data => handleCreate(data)}
                    onCancelForm={handleCloseFormModal}
                />
            </ModalBuild>

            <ModalBuild
                ref={deleteModalRef}
                closeOnOverlayClick={false}
                title="Confirmação de exclusão"
                size="md"
                actions={{
                    position: 'flex-end',
                    childrens: [
                        {
                            text: 'Cancelar',
                            handle: 'cancel',
                            variant: 'outline'
                        },
                        {
                            text: 'Deletar',
                            handle: 'confirm',
                            colorScheme: 'red'
                        }
                    ]
                }}
                onConfirm={handleDelete}
                onCancel={() => toogleDeleteModal(false)}
            >
                <Text px={6} py={4}>
                    Você realmente deseja deletar? Uma vez deletado, não será
                    possível voltar atrás.
                </Text>
            </ModalBuild>
        </Box>
    )
}

export default Agent
